// =============================================================================
// constants/kpiConstants.js
// Action types and constants for the KPI module
// =============================================================================

// --- Sites with coordinates (map) ---
export const SITES_FETCH_REQUEST = 'SITES_FETCH_REQUEST';
export const SITES_FETCH_SUCCESS = 'SITES_FETCH_SUCCESS';
export const SITES_FETCH_FAILURE = 'SITES_FETCH_FAILURE';

// --- Incidents ---
export const INCIDENTS_FETCH_REQUEST = 'INCIDENTS_FETCH_REQUEST';
export const INCIDENTS_FETCH_SUCCESS = 'INCIDENTS_FETCH_SUCCESS';
export const INCIDENTS_FETCH_FAILURE = 'INCIDENTS_FETCH_FAILURE';

export const INCIDENT_HISTORY_FETCH_REQUEST = 'INCIDENT_HISTORY_FETCH_REQUEST';
export const INCIDENT_HISTORY_FETCH_SUCCESS = 'INCIDENT_HISTORY_FETCH_SUCCESS';
export const INCIDENT_HISTORY_FETCH_FAILURE = 'INCIDENT_HISTORY_FETCH_FAILURE';

export const INCIDENT_SELECT        = 'INCIDENT_SELECT';
export const INCIDENT_FILTER_CHANGE = 'INCIDENT_FILTER_CHANGE';
export const INCIDENT_FILTER_RESET  = 'INCIDENT_FILTER_RESET';
// NOTE (fix5): INCIDENT_PAGE_CHANGE is gone — there is no more server-side
// pagination. The table now paginates the already-fetched, already-filtered
// result set in memory (plain MUI DataGrid client pagination).

// --- Lookups (vendors, techs, kpis, severities, statuses from the DB) ---
export const LOOKUPS_FETCH_REQUEST = 'LOOKUPS_FETCH_REQUEST';
export const LOOKUPS_FETCH_SUCCESS = 'LOOKUPS_FETCH_SUCCESS';
export const LOOKUPS_FETCH_FAILURE = 'LOOKUPS_FETCH_FAILURE';

// --- KPI Rules ---
export const RULES_FETCH_REQUEST = 'RULES_FETCH_REQUEST';
export const RULES_FETCH_SUCCESS = 'RULES_FETCH_SUCCESS';
export const RULES_FETCH_FAILURE = 'RULES_FETCH_FAILURE';

export const RULE_SAVE_REQUEST = 'RULE_SAVE_REQUEST';
export const RULE_SAVE_SUCCESS = 'RULE_SAVE_SUCCESS';
export const RULE_SAVE_FAILURE = 'RULE_SAVE_FAILURE';

export const RULE_DELETE_REQUEST = 'RULE_DELETE_REQUEST';
export const RULE_DELETE_SUCCESS = 'RULE_DELETE_SUCCESS';
export const RULE_DELETE_FAILURE = 'RULE_DELETE_FAILURE';

export const RULE_SELECT     = 'RULE_SELECT';
export const RULE_FORM_RESET = 'RULE_FORM_RESET';

export const FORMULA_VALIDATE_REQUEST = 'FORMULA_VALIDATE_REQUEST';
export const FORMULA_VALIDATE_SUCCESS = 'FORMULA_VALIDATE_SUCCESS';
export const FORMULA_VALIDATE_FAILURE = 'FORMULA_VALIDATE_FAILURE';
export const FORMULA_VALIDATE_RESET   = 'FORMULA_VALIDATE_RESET';

// --- Engine Logs ---
export const ENGINE_LOGS_FETCH_REQUEST = 'ENGINE_LOGS_FETCH_REQUEST';
export const ENGINE_LOGS_FETCH_SUCCESS = 'ENGINE_LOGS_FETCH_SUCCESS';
export const ENGINE_LOGS_FETCH_FAILURE = 'ENGINE_LOGS_FETCH_FAILURE';

// =============================================================================
// UI options
// =============================================================================

export const TECH_OPTIONS = ['2G', '3G', '4G', '5G'];

export const STATUS_OPTIONS = [
	{ value: 'Open',   label: 'Open'   },
	{ value: 'Closed', label: 'Closed' },
];

export const DIRECTION_OPTIONS = [
	{ value: false, label: 'A drop indicates a problem (Availability, Setup SR, Throughput)' },
	{ value: true,  label: 'A rise indicates a problem (Drop Rate, Block Rate, PRB Utilization)' },
];

export const DETECTION_METHOD_OPTIONS = [
	{
		value: 'PERCENTILE',
		label: 'Percentile (recommended)',
		description:
			'Compares the current KPI against this cell’s historical distribution (P95/P5). '
			+ 'Robust to non-normal distributions (Drop Rate, Block Rate). '
			+ 'The alarm only fires when the KPI crosses the absolute SLA AND its own percentile at the same time.',
	},
	{
		value: 'THRESHOLD',
		label: 'Threshold (fixed SLA)',
		description:
			'Fires only when the KPI crosses the absolute SLA limit, with no comparison against history. '
			+ 'Use when there is a strict contractual SLA (e.g. Availability < 98.5%).',
	},
	{
		value: 'ZSCORE',
		label: 'Z-Score (classic statistical)',
		description:
			'Compares the KPI against the historical mean ± 2.5 standard deviations. '
			+ 'Recommended for Availability (distribution close to 100% with a small standard deviation). '
			+ 'Less robust than Percentile for Drop Rates.',
	},
];

export const SEVERITY_OPTIONS = [
	{ value: 1, label: 'Critical', color: '#d32f2f' },
	{ value: 2, label: 'High',     color: '#ed6c02' },
	{ value: 3, label: 'Medium',   color: '#0288d1' },
];

// FIX5 — the old single ">7 days (chronic)" bucket was hiding just how much
// data lives in "chronic": ~28,900 rows for Tech=4G alone with no KPI
// filter. Split into finer ranges so the operator can actually narrow down
// within that universe instead of getting one giant, unfilterable bucket.
export const DURATION_OPTIONS = [
	{ value: 'all',    label: 'Any duration' },
	{ value: 'lt1',    label: '< 1 day (today)' },
	{ value: '1to3',   label: '1 to 3 days' },
	{ value: '4to7',   label: '4 to 7 days' },
	{ value: '8to30',  label: '8 to 30 days' },
	{ value: '31to90', label: '31 to 90 days' },
	{ value: 'gt90',   label: '> 90 days (chronic)' },
];

export const TECH_COLOR = {
	'2G': '#f59e0b',
	'3G': '#3b82f6',
	'4G': '#10b981',
	'5G': '#8b5cf6',
};

// =============================================================================
// Support text (tooltips) shown on the rule form fields
// =============================================================================

export const TOOLTIPS = {
	detectionMethod:
		'Statistical method to apply.\n\n'
		+ 'Percentile: compares the KPI against this cell’s historical percentiles (P95/P5).\n'
		+ 'Threshold: uses only the absolute SLA.\n'
		+ 'Z-Score: compares against the mean and standard deviation.\n\n'
		+ 'Recommended for Drop Rates: Percentile.\n'
		+ 'Recommended for Availability: Z-Score.',

	slaThreshold:
		'Absolute limit based on the contractual SLA.\n\n'
		+ '"A rise indicates a problem": the absolute criterion fires when KPI > SLA.\n'
		+ '"A drop indicates a problem": the absolute criterion fires when KPI < SLA.\n\n'
		+ 'With the Percentile method, this value is combined with the percentile (AND).\n'
		+ 'With the Threshold method, it is the only condition.\n\n'
		+ 'Examples: Voice Drop Rate 4G = 0.8 (0.8%) · Voice Setup SR 4G = 99.0 · Availability = 98.5',

	direction:
		'Direction in which the KPI degrades.\n\n'
		+ 'A rise indicates a problem: for KPIs where a higher value is worse (Drop Rate, Block Rate, PRB Utilization).\n\n'
		+ 'A drop indicates a problem: for KPIs where a lower value is worse (Availability, Setup SR, Throughput, CQI).',

	minVolumeAbsolute:
		'Minimum statistical floor — not the expected production volume.\n\n'
		+ 'The filter applied by the engine is always the larger of this value and 30% of the cell’s historical average volume:\n'
		+ 'MAX(this value, Historical Average Volume × 0.30)\n\n'
		+ 'Examples with this value set to 20:\n'
		+ '· Urban cell (average volume 5000) → effective filter = 1500\n'
		+ '· Rural cell (average volume 80) → effective filter = 24\n'
		+ '· Small rural cell (average volume 30) → effective filter = 20\n\n'
		+ 'Typical values by technology: 2G = 15 · 3G = 20 · 4G = 20 · 5G = 15.',

	useDayOfWeek:
		'Should the baseline account for the day of the week?\n\n'
		+ 'Yes (recommended for Drop Rate, Throughput, Setup SR): the baseline uses the 12 most recent occurrences of the same day of the week, isolating weekly patterns (e.g. Friday night vs. Sunday).\n\n'
		+ 'No (recommended for Availability): the baseline uses the last 28 consecutive days. Suitable for KPIs whose variability does not depend on the day of the week (equipment faults, alarms).',

	severity:
		'Operational severity assigned to the alarm raised by this rule.\n\n'
		+ 'Critical: direct impact on service — Availability, Drop Rates.\n'
		+ 'High: impact on customer experience — Setup SR, Block Rates.\n'
		+ 'Medium: quality indicators — Throughput, CQI.',
};

// =============================================================================
// Preset profiles — used by the "Apply preset profile" control
// =============================================================================

export const KPI_PROFILES = [
	{
		key: 'drop_rate_2g',
		label: 'Drop Rate · 2G',
		preset: {
			detectionMethod:   'PERCENTILE',
			slaThreshold:      2.0,
			direction:         true,
			minVolumeAbsolute: 15,
			useDayOfWeek:      true,
			severity:          1,
		},
	},
	{
		key: 'drop_rate_3g',
		label: 'Drop Rate · 3G',
		preset: {
			detectionMethod:   'PERCENTILE',
			slaThreshold:      1.0,
			direction:         true,
			minVolumeAbsolute: 20,
			useDayOfWeek:      true,
			severity:          1,
		},
	},
	{
		key: 'drop_rate_4g',
		label: 'Drop Rate · 4G',
		preset: {
			detectionMethod:   'PERCENTILE',
			slaThreshold:      0.8,
			direction:         true,
			minVolumeAbsolute: 20,
			useDayOfWeek:      true,
			severity:          1,
		},
	},
	{
		key: 'drop_rate_5g',
		label: 'Drop Rate · 5G',
		preset: {
			detectionMethod:   'PERCENTILE',
			slaThreshold:      1.2,
			direction:         true,
			minVolumeAbsolute: 15,
			useDayOfWeek:      true,
			severity:          1,
		},
	},
	{
		key: 'setup_sr_voice',
		label: 'Voice Setup SR',
		preset: {
			detectionMethod:   'PERCENTILE',
			slaThreshold:      99.0,
			direction:         false,
			minVolumeAbsolute: 20,
			useDayOfWeek:      true,
			severity:          2,
		},
	},
	{
		key: 'setup_sr_data',
		label: 'Data Setup SR',
		preset: {
			detectionMethod:   'PERCENTILE',
			slaThreshold:      97.0,
			direction:         false,
			minVolumeAbsolute: 20,
			useDayOfWeek:      true,
			severity:          2,
		},
	},
	{
		key: 'availability',
		label: 'Availability',
		preset: {
			detectionMethod:   'ZSCORE',
			slaThreshold:      98.5,
			direction:         false,
			minVolumeAbsolute: 20,
			useDayOfWeek:      false,
			severity:          1,
		},
	},
];

// ============================================================================
// Initial values for a new rule
// ============================================================================

export const EMPTY_RULE = {
	kpiId:             0,
	kpiName:           '',
	tech:              '4G',
	sourceTable:       '',
	sourceVendorCol:   'VENDOR',
	sourceSiteCol:     'JV_ID',
	sourceCellCol:     'MI_MYCOM_ID',
	sourceDateCol:     'DATE',
	kpiFormula:        '',
	volFormula:        '',
	detectionMethod:   'PERCENTILE',
	slaThreshold:      0,
	direction:         true,
	minVolumeAbsolute: 20,
	useDayOfWeek:      true,
	severity:          2,
	active:            true,
};